/**
 * The shape every public page is built from.
 *
 * Nine marketing pages had each grown their own heading sizes, their own gap
 * between sections and their own end-of-page box, so the site read as nine
 * pages rather than one. These are the four parts they actually share — the
 * opening, a section, a list of claims, and the box the button lives in — and
 * a page that uses them gets the rhythm for free.
 *
 * Nothing here decides what a page says. The eyebrow, the heading and the lead
 * are passed in; what they may claim is a question for the copy, not for the
 * layout.
 */
import type { ReactNode } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { navigate } from "@/lib/router";
import { track } from "@/lib/analytics";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

/**
 * The page's outer stack, with a soft wash behind the opening.
 *
 * The wash is `--muted` fading to nothing: the palette is deliberately neutral
 * (near-black on stone), so anything coloured here would be the one saturated
 * thing on the site. It exists to give the first screen a horizon, not to
 * decorate.
 */
export function MarketingPage({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("relative flex flex-col gap-14 pb-8", className)}>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-[-50vw] top-[-6rem] -z-10 h-[26rem] bg-[radial-gradient(55%_60%_at_50%_0%,var(--muted),transparent_70%)]"
      />
      {children}
    </div>
  );
}

/** The primary call to action. One per page — every public page sends the
 *  reader to the same door, except `/pricing`, which sends them to checkout. */
export function StartButton({
  className,
  to = "/app",
  label,
  placement = "panel",
}: {
  className?: string;
  to?: string;
  label?: string;
  /** Which copy of the page's one button this is, for `cta_click`. */
  placement?: "hero" | "panel";
}) {
  const { t } = useI18n();
  return (
    <Button
      size="lg"
      variant="brand"
      className={cn("h-11", className)}
      onClick={() => {
        track("cta_click", { placement, target: to });
        navigate(to);
      }}
    >
      {label ?? t("landing.start")}
      <ArrowRight className="size-4" />
    </Button>
  );
}

/**
 * The opening: what this page is, in one heading and one paragraph, with the
 * claims under it and the button under those.
 */
export function Hero({
  eyebrow,
  title,
  lead,
  points,
  note,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  lead: ReactNode;
  points?: ReactNode[];
  note?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="pt-4">
      {/* The tagline is a sentence, not a tag: on a phone it is wider than the
          screen, and the base badge never wraps. */}
      {eyebrow && (
        <Badge variant="secondary" className="mb-4 whitespace-normal text-left">
          {eyebrow}
        </Badge>
      )}
      <h1 className="text-balance text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl">
        {title}
      </h1>
      <p className="mt-5 max-w-[56ch] text-pretty text-lg leading-relaxed text-muted-foreground">
        {lead}
      </p>
      {points && points.length > 0 && (
        <ul className="mt-6 flex max-w-[58ch] flex-col gap-3 border-l-2 pl-4">
          {points.map((point, i) => (
            <li key={i} className="flex gap-2.5 leading-relaxed">
              <Check className="mt-1 size-4 shrink-0 text-correct" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      )}
      {children ?? <div className="mt-8">{<StartButton placement="hero" />}</div>}
      {note && (
        <p className="mt-3 max-w-[54ch] text-sm leading-relaxed text-muted-foreground">{note}</p>
      )}
    </section>
  );
}

/** One block of the page: an optional eyebrow, a heading, an optional lead. */
export function Section({
  eyebrow,
  title,
  lead,
  className,
  eyebrowClassName,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  className?: string;
  /** Overrides the eyebrow's colour — the landing sets its own. */
  eyebrowClassName?: string;
  children: ReactNode;
}) {
  return (
    <section className={className}>
      {eyebrow && (
        <p
          className={cn(
            "mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground",
            eyebrowClassName,
          )}
        >
          {eyebrow}
        </p>
      )}
      <h2 className="text-balance text-2xl font-bold tracking-tight">{title}</h2>
      {lead && (
        <p className="mt-2 max-w-[64ch] leading-relaxed text-muted-foreground">{lead}</p>
      )}
      <div className="mt-5">{children}</div>
    </section>
  );
}

/**
 * A body of copy that may run to more than one paragraph.
 *
 * A catalogue string is one string, and a `<p>` collapses the `\n\n` inside it
 * into a space — so a block that grew past three sentences could only be split
 * by adding a second key and a second element to every page that renders it.
 * The legal pages had already solved this by splitting on the blank line; this
 * is the same rule, available to the marketing pages that now need it.
 */
export function Prose({ text, className }: { text: string; className?: string }) {
  return (
    <>
      {text.split("\n\n").map((para, i) => (
        <p
          key={i}
          className={cn(
            "max-w-[64ch] leading-relaxed text-muted-foreground",
            i > 0 && "mt-3",
            className,
          )}
        >
          {para}
        </p>
      ))}
    </>
  );
}

/**
 * A claim per card. Used where a block is a list of independent reasons rather
 * than a paragraph — four reasons read as four things in a grid and as one
 * long sentence in a bulleted list.
 */
export function ClaimCards({
  items,
  columns = 2,
}: {
  /** `wide`: across both columns — an odd one out at the end of a two-column grid. */
  items: { key: string; title?: string; text: ReactNode; wide?: boolean }[];
  columns?: 1 | 2;
}) {
  return (
    <div className={cn("grid gap-3", columns === 2 && "sm:grid-cols-2")}>
      {items.map((item) => (
        <div
          key={item.key}
          className={cn("rounded-xl border bg-card p-4", item.wide && columns === 2 && "sm:col-span-2")}
        >
          {item.title && <p className="mb-1.5 font-medium">{item.title}</p>}
          <p className="text-sm leading-relaxed text-muted-foreground">{item.text}</p>
        </div>
      ))}
    </div>
  );
}

/** The box at the foot of the page: why to press the button, and the button. */
export function CtaPanel({
  title,
  text,
  note,
  children,
}: {
  title: ReactNode;
  text?: ReactNode;
  note?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="rounded-2xl border bg-muted/40 p-6 sm:p-8">
      <p className="text-xl font-bold tracking-tight">{title}</p>
      {text && (
        <p className="mt-2 max-w-[56ch] leading-relaxed text-muted-foreground">{text}</p>
      )}
      {children ?? <StartButton className="mt-6" />}
      {note && <p className="mt-3 text-sm text-muted-foreground">{note}</p>}
    </section>
  );
}
